import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero, PageLayout } from "../components/PageShell";
import { ROUTES } from "../lib/routes";
import { SCHOLARSHIP_COUNT, SCHOLARSHIPS, SCHOLARSHIP_INTRO } from "../lib/scholarships";

const SECTIONS = [
  { id: "scholarships", label: "Scholarships" },
  { id: "applying", label: "How to apply" },
];

export default function Scholarships() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  useEffect(() => {
    const els = SECTIONS
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -58% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const withLink = SCHOLARSHIPS.filter((s) => s.link).length;

  return (
    <>
      <PageHero
        variant="hero-navy"
        eyebrow="Student support"
        title="Help with fees, from the state, the centre and private trusts."
        lead="Government, corporate-CSR and private-trust scholarships open to BIET students. Apply directly through each provider — this page names who that is and links to their portal where one is published."
        crumbs={[{ label: "Campus Life", to: ROUTES.campusLife }, { label: "Scholarships" }]}
        /* Nothing to illustrate — the hero carries the shape of the list. */
        image={null}
        aside={
          <div className="hero-facts">
            <div className="hf-row">
              <span className="hf-num">{SCHOLARSHIP_COUNT}</span>
              <span className="hf-lbl">Scholarships</span>
            </div>
            <div className="hf-row">
              <span className="hf-num">{withLink}</span>
              <span className="hf-lbl">With a direct link</span>
            </div>
          </div>
        }
      />

      <PageLayout
        side={[
          {
            title: "On this page",
            items: SECTIONS.map((s) => ({
              label: s.label,
              to: `#${s.id}`,
              active: activeId === s.id,
            })),
          },
          {
            title: "Related",
            items: [
              { label: "Fee structure", to: ROUTES.admissions },
              { label: "Hostels & mess", to: ROUTES.hostels },
              { label: "Contact the office", to: ROUTES.contact },
            ],
          },
        ]}
      >
        <h2 id="scholarships">Scholarships</h2>
        <p>{SCHOLARSHIP_INTRO}</p>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Scholarship</th>
                <th scope="col">Provider</th>
                <th scope="col">Link</th>
              </tr>
            </thead>
            <tbody>
              {SCHOLARSHIPS.map((s) => (
                <tr key={s.name}>
                  <th scope="row">
                    {s.name}
                    {s.note && <div className="sch-note">{s.note}</div>}
                  </th>
                  <td>{s.provider}</td>
                  <td>
                    {s.link ? (
                      <a href={s.link} target="_blank" rel="noreferrer noopener">
                        Apply <span aria-hidden="true">↗</span>
                      </a>
                    ) : (
                      <span style={{ color: "var(--text-muted)" }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 id="applying">How to apply</h2>
        <p>
          Applications are made to the provider named against each scholarship, not to the
          institute — BIET's role is to certify your admission, attendance and fee payment on the
          form. The office can tell you which scholarships you are eligible for and what each one
          currently requires.
        </p>
        <div className="report-card" style={{ marginTop: "var(--space-6)" }}>
          <div>
            <h3>Ask the office</h3>
            <p>
              For help identifying the right scheme, or for the certificates a
              provider needs from the institute.
            </p>
          </div>
          <div className="actions">
            <span className="hot">INSTITUTE OFFICE</span>
            <span className="num">+91 8192 233 800</span>
            <a
              className="btn-link"
              href="mailto:info@bietdvg.edu"
              style={{ color: "var(--gold-400)", borderBottomColor: "var(--gold-400)" }}
            >
              info@bietdvg.edu →
            </a>
          </div>
        </div>

        <p className="sch-source">
          Schemes, eligibility and providers change — confirm with them directly before applying.
          Fee details are on the{" "}
          <Link to={ROUTES.admissions}>Admissions</Link> page.
        </p>
      </PageLayout>
    </>
  );
}
